// Copyright 2026. WebPros International GmbH. All rights reserved.

import { PropsValue } from 'react-select';
import { ISelectRequiredOption } from 'common/components';
import * as React from 'react';
import { FILTERS } from 'admin/common/constants/tests';
import { FixedFormField } from 'admin/common/components/Filters/Styles';
import SelectInput from 'common/components/Select/SelectInput';
import {
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import { HighAvailabilityStatus } from 'common/api/resources/HighAvailability';

export interface IHighAvailabilityHistoryStatusFilterProps {
    onChange?: (option: PropsValue<ISelectRequiredOption>) => void;
    value?: PropsValue<ISelectRequiredOption>;
}

const statusTranslationKeys: Record<HighAvailabilityStatus, string> = {
    [HighAvailabilityStatus.PENDING]: 'highAvailability.activities.status.pending',
    [HighAvailabilityStatus.RUNNING]: 'highAvailability.activities.status.running',
    [HighAvailabilityStatus.RUNNING_WITH_ERRORS]: 'highAvailability.activities.status.runningWithErrors',
    [HighAvailabilityStatus.COMPLETED]: 'highAvailability.activities.status.completed',
    [HighAvailabilityStatus.COMPLETED_WITH_ERRORS]: 'highAvailability.activities.status.completedWithErrors',
    [HighAvailabilityStatus.FAILED]: 'highAvailability.activities.status.failed',
    [HighAvailabilityStatus.CANCELLED]: 'highAvailability.activities.status.cancelled',
};

export const HighAvailabilityHistoryStatusFilter: React.FC<IHighAvailabilityHistoryStatusFilterProps> = ({
    onChange,
    value,
}) => {
    const translate = useTranslate();

    const statuses: ISelectRequiredOption[] = React.useMemo(() =>
        Object.values(HighAvailabilityStatus).map((status) => ({
            value: status,
            label: translate(statusTranslationKeys[status]) as string,
        })),
    [translate]);

    return (
        <FixedFormField data-cy={FILTERS.HIGH_AVAILABILITY_HISTORY_STATUS}>
            <SelectInput<ISelectRequiredOption>
                inputId="ha-activity-status-filter"
                placeholder={<Translate content="filters.status"/>}
                options={statuses}
                isClearable={true}
                onChange={onChange}
                value={value}
                menuPosition="absolute"
                menuPortalTarget={document.body}
            />
        </FixedFormField>
    );
};
